import type { SearchCatalogItemsQuery } from "@albertine/types/openapi/lmt";
import { excludeUndefined } from "../utils/array.util";
import { encodeKeyValuePair } from "../utils/url.util";

type OpenSearchQuery = Record<string, unknown>;

type SlugsFilter = {
    terms: {
        slug: string[];
    };
};

function filterBySlugs(slugs: string[]): SlugsFilter {
    return {
        terms: {
            slug: slugs,
        },
    };
}

export type ItemsBySlugsQuery = {
    query: {
        bool: {
            filter: [SlugsFilter];
        };
    };
} & OpenSearchQuery;

export function itemsBySlugsQuery(slugs: string[]): ItemsBySlugsQuery {
    return {
        from: 0,
        size: slugs.length,
        query: {
            bool: {
                filter: [filterBySlugs(slugs)],
            },
        },
    };
}

export function fromCatalogQueryFiltersToUrlSearch({
    filter,
    locationSearchTerm,
    recommendedItemIds,
    viewAiRecommendations,
}: {
    filter: SearchCatalogItemsQuery | undefined;
    locationSearchTerm: string | undefined;
    recommendedItemIds: string[] | undefined;
    viewAiRecommendations?: boolean;
}): string | undefined {
    const categories = filter?.categories
        ? filter?.categories
              ?.map((it, index) =>
                  encodeKeyValuePair(`offeringCategories[${index}]`, it),
              )
              .join("&")
        : undefined;

    const recommendedItemIdsParam = recommendedItemIds?.length
        ? recommendedItemIds
              .map((it, index) =>
                  encodeKeyValuePair(`recommendedItemIds[${index}]`, it),
              )
              .join("&")
        : undefined;

    const locationSearchTermParam = locationSearchTerm
        ? encodeKeyValuePair("locationSearchTerm", locationSearchTerm)
        : undefined;

    const viewAiRecommendationsParam = viewAiRecommendations
        ? encodeKeyValuePair("viewAiRecommendations", "1")
        : undefined;

    const countryParam = filter?.country
        ? encodeKeyValuePair("country", filter.country)
        : undefined;
    const latitudeParam = filter?.latitude
        ? encodeKeyValuePair("lat", filter.latitude)
        : undefined;
    const longitudeParam = filter?.longitude
        ? encodeKeyValuePair("lon", filter.longitude)
        : undefined;
    const geoDistanceParam = filter?.maxGeoDistanceKm
        ? encodeKeyValuePair("maxGeoDistanceKm", filter.maxGeoDistanceKm)
        : undefined;
    const discoverableForTenantParam = filter?.discoverableForTenant
        ? encodeKeyValuePair("discoverableForTenant", filter.discoverableForTenant)
        : undefined;

    const searchTerms = [
        categories,
        recommendedItemIdsParam,
        locationSearchTermParam,
        viewAiRecommendationsParam,
        countryParam,
        latitudeParam,
        longitudeParam,
        geoDistanceParam,
        discoverableForTenantParam,
    ]
        .filter(excludeUndefined)
        .join("&");

    return searchTerms.length !== 0 ? searchTerms : undefined;
}
